import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/esm/Edit';
import DeleteIcon from '@mui/icons-material/esm/Delete';
import CheckIcon from '@mui/icons-material/esm/Check';
import CloseIcon from '@mui/icons-material/esm/Close';
import ExpandMoreIcon from '@mui/icons-material/esm/ExpandMore';
import PersonAddIcon from '@mui/icons-material/esm/PersonAdd';
import ContentCopyIcon from '@mui/icons-material/esm/ContentCopy';
import LinkIcon from '@mui/icons-material/esm/Link';
import { PageHeader } from '../../components/layout';
import { KPICard } from '../../components/common';
import { RootState } from '../../store';
import projectService, { ProjectListItem } from '../../services/api/projectService';
import teamService, { TeamListItem, CreateTeamPayload } from '../../services/api/teamService';
import userService, { UserListItem, CreateUserPayload } from '../../services/api/userService';
import telegramService from '../../services/api/telegramService';
import inventoryMovementService, {
  InventoryMovementItem,
  CreateInventoryMovementPayload,
  CreateInventoryAdjustmentPayload,
} from '../../services/api/inventoryMovementService';
import workLocationsService, { WorkLocation } from '../../services/api/workLocationsService';
import TeamsOverviewPage from '../teamsOverview/TeamsOverviewPage';

type AdminTab =
  | 'users'
  | 'teams'
  | 'teams-overview'
  | 'projects'
  | 'resources'
  | 'cost-codes'
  | 'storage'
  | 'work-locations'
  | 'suppliers';

const TEAM_TYPES = ['stores', 'construction', 'supervisor', 'accounts', 'procurement', 'sales'];

export default function AdminPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const initialTab = new URLSearchParams(location.search).get('tab') === 'teams-overview' ? 'teams-overview' : 'users';
  const [tab, setTab] = useState<AdminTab>(initialTab);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [teams, setTeams] = useState<TeamListItem[]>([]);
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [projectMembers, setProjectMembers] = useState<{
    id: string;
    projectId: string;
    userId: string;
    joinedAt?: string;
    user?: { id: string; email: string; firstName: string; lastName: string; role: string };
  }[]>([]);
  const [projectMemberUserId, setProjectMemberUserId] = useState('');
  const [movements, setMovements] = useState<InventoryMovementItem[]>([]);
  const [workLocations, setWorkLocations] = useState<WorkLocation[]>([]);
  const [newLocationName, setNewLocationName] = useState('');
  const [editingLocationId, setEditingLocationId] = useState<string | null>(null);
  const [editingLocationName, setEditingLocationName] = useState('');
  const [teamForm, setTeamForm] = useState<CreateTeamPayload>({
    name: '',
    teamType: 'construction',
    description: '',
    maxConcurrentTasks: 4,
  });
  const [movementForm, setMovementForm] = useState<CreateInventoryMovementPayload>({
    resourceId: '',
    storageLocationId: '',
    movementType: 'receipt',
    sourceType: 'manual',
    quantity: 1,
    notes: '',
  });
  const [adjustmentForm, setAdjustmentForm] = useState<CreateInventoryAdjustmentPayload>({
    inventoryId: '',
    newQuantity: 0,
    reasonCode: 'manual_count',
    notes: '',
  });
  // Per-team "add member" draft userId, keyed by team id, so each accordion
  // has its own independent input. (A typed user-picker is on a later
  // branch — fall back to User ID until that lands here.)
  const [memberDrafts, setMemberDrafts] = useState<Record<string, string>>({});

  // Workers / messaging directory state. Edits to email/phone/Telegram are inline
  // per row; creates use the workerForm at the top of the section.
  const [workers, setWorkers] = useState<UserListItem[]>([]);
  const [workerForm, setWorkerForm] = useState<CreateUserPayload>({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: 'construction',
    password: '',
  });
  const [editingWorkerId, setEditingWorkerId] = useState<string | null>(null);
  const [editingWorkerEmail, setEditingWorkerEmail] = useState('');
  const [editingWorkerPhone, setEditingWorkerPhone] = useState('');
  const [editingWorkerTelegramChatId, setEditingWorkerTelegramChatId] = useState('');
  const [telegramLinks, setTelegramLinks] = useState<Record<string, string>>({});
  // Inline error/success bound to the Add Worker form so feedback shows
  // next to the form instead of off-screen at the top of the page.
  const [workerFormError, setWorkerFormError] = useState<string | null>(null);
  const [workerFormSuccess, setWorkerFormSuccess] = useState<string | null>(null);
  // Per-team add-member inline error, keyed by team id.
  const [memberAddError, setMemberAddError] = useState<Record<string, string>>({});

  const loadAdminData = async () => {
    setLoading(true);
    setError(null);
    // Promise.allSettled (not Promise.all) so one failing sub-call —
    // e.g. /users returning 403 for a role with no listing access —
    // doesn't wipe out the others. Without this, creating a team and
    // calling loadAdminData would silently fail to refresh the team
    // list because one of the unrelated parallel calls rejected.
    const [teamRes, projectRes, movementRes, locationRes, workerRes] = await Promise.allSettled([
      teamService.getTeams(),
      projectService.getProjects(),
      inventoryMovementService.getMovements(),
      workLocationsService.list(true),
      userService.searchUsers({ limit: 50 }),
    ]);

    const errs: string[] = [];
    if (teamRes.status === 'fulfilled') setTeams(teamRes.value); else errs.push('teams');
    if (projectRes.status === 'fulfilled') setProjects(projectRes.value); else errs.push('projects');
    if (movementRes.status === 'fulfilled') setMovements(movementRes.value); else errs.push('inventory');
    if (locationRes.status === 'fulfilled') setWorkLocations(locationRes.value); else errs.push('work locations');
    if (workerRes.status === 'fulfilled') setWorkers(workerRes.value); else errs.push('workers');

    if (errs.length > 0) {
      setError(`Some admin data did not load: ${errs.join(', ')}`);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadAdminData();
  }, []);

  useEffect(() => {
    if (new URLSearchParams(location.search).get('tab') === 'teams-overview') {
      setTab('teams-overview');
    }
  }, [location.search]);

  useEffect(() => {
    if (!selectedProjectId && projects[0]?.id) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId]);

  useEffect(() => {
    const loadMembers = async () => {
      if (!selectedProjectId) {
        setProjectMembers([]);
        return;
      }

      try {
        const members = await projectService.getProjectMembers(selectedProjectId);
        setProjectMembers(members);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load project members');
      }
    };

    loadMembers();
  }, [selectedProjectId]);

  const counts = useMemo(
    () => ({
      teams: teams.length,
      projects: projects.length,
      movements: movements.length,
      managedBy: user ? `${user.firstName} ${user.lastName}` : 'Unauthenticated',
    }),
    [projects.length, teams.length, movements.length, user]
  );

  const handleCreateTeam = async () => {
    try {
      setError(null);
      setSuccess(null);
      const created = await teamService.createTeam(teamForm);
      setSuccess(`Team "${created?.name || teamForm.name}" created.`);
      setTeamForm({
        name: '',
        teamType: 'construction',
        description: '',
        maxConcurrentTasks: 4,
      });
      await loadAdminData();
    } catch (err: any) {
      const data = err?.response?.data;
      setError(
        data?.message || data?.error
          || (Array.isArray(data?.errors) ? data.errors.join(', ') : null)
          || err?.message
          || 'Failed to create team'
      );
    }
  };

  const handleAddTeamMember = async (teamId: string) => {
    const userId = (memberDrafts[teamId] || '').trim();
    if (!userId) {
      setMemberAddError({ ...memberAddError, [teamId]: 'Paste a User ID from the Users tab.' });
      return;
    }
    // Joi on the backend requires a UUID. Pre-validate so the error is
    // immediate and obvious instead of a generic "Failed to add member".
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidPattern.test(userId)) {
      setMemberAddError({
        ...memberAddError,
        [teamId]: 'That is not a User ID. Copy one from the User ID column in the Users tab.',
      });
      return;
    }
    try {
      setError(null);
      setSuccess(null);
      setMemberAddError({ ...memberAddError, [teamId]: '' });
      await teamService.addTeamMember(teamId, userId);
      setMemberDrafts({ ...memberDrafts, [teamId]: '' });
      setSuccess('Team member added.');
      await loadAdminData();
    } catch (err: any) {
      const data = err?.response?.data;
      const msg = data?.message || data?.error
        || (Array.isArray(data?.errors) ? data.errors.join(', ') : null)
        || err?.message
        || 'Failed to add team member';
      setMemberAddError({ ...memberAddError, [teamId]: msg });
    }
  };

  const handleRemoveTeamMember = async (teamId: string, userId: string, label: string) => {
    if (!window.confirm(`Remove ${label} from this team?`)) return;
    try {
      setError(null);
      setSuccess(null);
      await teamService.removeTeamMember(teamId, userId);
      setSuccess('Team member removed.');
      await loadAdminData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove team member');
    }
  };

  const handleDeleteTeam = async (teamId: string, teamName: string) => {
    if (!window.confirm(
      `Delete team "${teamName}"? It will be deactivated; existing task references are kept.`
    )) return;
    try {
      setError(null);
      setSuccess(null);
      await teamService.deleteTeam(teamId);
      setSuccess('Team deleted.');
      await loadAdminData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete team');
    }
  };

  const handleCreateWorker = async () => {
    const trimmedEmail = workerForm.email.trim();
    const trimmedFirst = workerForm.firstName.trim();
    const trimmedLast = workerForm.lastName.trim();
    const trimmedPassword = workerForm.password.trim();

    setWorkerFormError(null);
    setWorkerFormSuccess(null);

    if (!trimmedEmail || !trimmedFirst || !trimmedLast || !trimmedPassword || !workerForm.role) {
      setWorkerFormError('First name, last name, email, role, and password are all required.');
      return;
    }
    if (trimmedPassword.length < 8) {
      setWorkerFormError('Password must be at least 8 characters.');
      return;
    }
    try {
      const created = await userService.createUser({
        email: trimmedEmail,
        firstName: trimmedFirst,
        lastName: trimmedLast,
        phone: workerForm.phone?.trim() || undefined,
        role: workerForm.role,
        password: trimmedPassword,
      });
      const teamCount = created.assignedTeamIds?.length ?? 0;
      setWorkerFormSuccess(
        teamCount > 0
          ? `Worker ${trimmedFirst} ${trimmedLast} added and placed on ${teamCount} ${workerForm.role} team${teamCount === 1 ? '' : 's'}.`
          : `Worker ${trimmedFirst} ${trimmedLast} added. No matching ${workerForm.role} team exists — add them manually from the Teams tab if needed.`
      );
      setWorkerForm({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        role: 'construction',
        password: '',
      });
      await loadAdminData();
    } catch (err: any) {
      const data = err?.response?.data;
      setWorkerFormError(
        data?.message || data?.error
          || (Array.isArray(data?.errors) ? data.errors.join(', ') : null)
          || err?.message
          || 'Failed to add worker'
      );
    }
  };

  const handleStartEditWorker = (worker: UserListItem) => {
    setEditingWorkerId(worker.id);
    setEditingWorkerEmail(worker.email);
    setEditingWorkerPhone(worker.phone || '');
    setEditingWorkerTelegramChatId(worker.telegramChatId || '');
  };

  const handleCancelEditWorker = () => {
    setEditingWorkerId(null);
    setEditingWorkerEmail('');
    setEditingWorkerPhone('');
    setEditingWorkerTelegramChatId('');
  };

  const handleSaveEditWorker = async (workerId: string) => {
    try {
      setError(null);
      setSuccess(null);
      await userService.updateUser(workerId, {
        email: editingWorkerEmail.trim(),
        phone: editingWorkerPhone.trim() || undefined,
        telegramChatId: editingWorkerTelegramChatId.trim() || null,
      });
      setSuccess('Worker updated.');
      handleCancelEditWorker();
      await loadAdminData();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to update worker');
    }
  };

  const handleCreateTelegramLink = async (worker: UserListItem) => {
    try {
      setError(null);
      setSuccess(null);
      const result = await telegramService.createLinkToken(worker.id);
      const link = result.link || result.token;
      setTelegramLinks({ ...telegramLinks, [worker.id]: link });
      if (result.link) {
        await navigator.clipboard?.writeText(result.link);
        setSuccess(`Telegram link for ${worker.firstName} ${worker.lastName} copied.`);
      } else {
        setSuccess(`Telegram link token generated for ${worker.firstName} ${worker.lastName}.`);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to create Telegram link');
    }
  };

  const handleRecordMovement = async () => {
    try {
      setError(null);
      setSuccess(null);
      await inventoryMovementService.createMovement(movementForm);
      setSuccess('Inventory movement recorded.');
      setMovementForm({
        resourceId: '',
        storageLocationId: '',
        movementType: 'receipt',
        sourceType: 'manual',
        quantity: 1,
        notes: '',
      });
      await loadAdminData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to record movement');
    }
  };

  const handleAdjustInventory = async () => {
    try {
      setError(null);
      setSuccess(null);
      await inventoryMovementService.createAdjustment(adjustmentForm);
      setSuccess('Inventory adjustment recorded.');
      setAdjustmentForm({
        inventoryId: '',
        newQuantity: 0,
        reasonCode: 'manual_count',
        notes: '',
      });
      await loadAdminData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to record adjustment');
    }
  };

  const handleAddProjectMember = async () => {
    if (!selectedProjectId || !projectMemberUserId.trim()) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      await projectService.addProjectMember(selectedProjectId, { userId: projectMemberUserId.trim() });
      setProjectMemberUserId('');
      setSuccess('Project member added.');
      const members = await projectService.getProjectMembers(selectedProjectId);
      setProjectMembers(members);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add project member');
    }
  };

  const handleRemoveProjectMember = async (userId: string) => {
    if (!selectedProjectId) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      await projectService.removeProjectMember(selectedProjectId, userId);
      setSuccess('Project member removed.');
      const members = await projectService.getProjectMembers(selectedProjectId);
      setProjectMembers(members);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove project member');
    }
  };

  // ---- Work locations ----

  const refreshWorkLocations = async () => {
    try {
      const list = await workLocationsService.list(true);
      setWorkLocations(list);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load work locations');
    }
  };

  const handleCreateWorkLocation = async () => {
    const name = newLocationName.trim();
    if (!name) return;
    try {
      setError(null);
      setSuccess(null);
      await workLocationsService.create({ name });
      setSuccess(`Created work location "${name}".`);
      setNewLocationName('');
      await refreshWorkLocations();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create work location');
    }
  };

  const handleStartEditWorkLocation = (loc: WorkLocation) => {
    setEditingLocationId(loc.id);
    setEditingLocationName(loc.name);
  };

  const handleSaveEditWorkLocation = async () => {
    if (!editingLocationId) return;
    const name = editingLocationName.trim();
    if (!name) return;
    try {
      setError(null);
      setSuccess(null);
      await workLocationsService.update(editingLocationId, { name });
      setSuccess('Work location updated.');
      setEditingLocationId(null);
      setEditingLocationName('');
      await refreshWorkLocations();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update work location');
    }
  };

  const handleDeleteWorkLocation = async (loc: WorkLocation) => {
    if (!window.confirm(`Delete work location "${loc.name}"? Any tasks pointing at it will lose the reference.`)) return;
    try {
      setError(null);
      setSuccess(null);
      await workLocationsService.remove(loc.id);
      setSuccess(`Deleted work location "${loc.name}".`);
      await refreshWorkLocations();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete work location');
    }
  };

  return (
    <Box>
      <PageHeader
        title="Admin"
        subtitle="Minimal Phase 1 admin surface for project, team, and inventory operations."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Teams" value={counts.teams} color="primary" loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Projects" value={counts.projects} color="info" loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Movements" value={counts.movements} color="success" loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Managed By" value={counts.managedBy} color="warning" loading={loading} />
        </Grid>
      </Grid>

      <Paper sx={{ borderRadius: 1, mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, next: AdminTab) => {
            setTab(next);
            navigate(next === 'teams-overview' ? '/admin?tab=teams-overview' : '/admin', { replace: true });
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="users" label="Users" />
          <Tab value="teams" label="Teams" />
          <Tab value="teams-overview" label="Teams Overview" />
          <Tab value="projects" label="Projects" />
          <Tab value="resources" label="Resources" />
          <Tab value="cost-codes" label="Cost Codes" />
          <Tab value="storage" label="Storage Locations" />
          <Tab value="work-locations" label="Work Locations" />
          <Tab value="suppliers" label="Suppliers" />
        </Tabs>
      </Paper>

      {tab === 'users' && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Current user
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="body2">
                    {user?.firstName} {user?.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user?.email}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Role: {user?.role}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Role changes are locked out in Phase 1.
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Workers - messaging directory
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  Telegram chat IDs are used for the primary external dispatch channel. Phone remains available for WhatsApp fallback.
                </Typography>

                {/* Add-worker form */}
                <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Add worker
                  </Typography>
                  {workerFormError && (
                    <Alert severity="error" sx={{ mb: 1 }} onClose={() => setWorkerFormError(null)}>
                      {workerFormError}
                    </Alert>
                  )}
                  {workerFormSuccess && (
                    <Alert severity="success" sx={{ mb: 1 }} onClose={() => setWorkerFormSuccess(null)}>
                      {workerFormSuccess}
                    </Alert>
                  )}
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        size="small"
                        fullWidth
                        label="First name"
                        value={workerForm.firstName}
                        onChange={(event) => setWorkerForm({ ...workerForm, firstName: event.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Last name"
                        value={workerForm.lastName}
                        onChange={(event) => setWorkerForm({ ...workerForm, lastName: event.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Email"
                        type="email"
                        value={workerForm.email}
                        onChange={(event) => setWorkerForm({ ...workerForm, email: event.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Phone"
                        placeholder="+1 876 555 0123"
                        value={workerForm.phone}
                        onChange={(event) => setWorkerForm({ ...workerForm, phone: event.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Role</InputLabel>
                        <Select
                          value={workerForm.role}
                          label="Role"
                          onChange={(event) => setWorkerForm({ ...workerForm, role: String(event.target.value) })}
                        >
                          {['admin', 'pm', 'stores', 'construction', 'supervisor', 'accounts', 'procurement', 'sales', 'qs'].map((r) => (
                            <MenuItem key={r} value={r}>{r}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Initial password"
                        type="password"
                        helperText="Min 8 characters. Worker can change this after first login."
                        value={workerForm.password}
                        onChange={(event) => setWorkerForm({ ...workerForm, password: event.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Button
                        variant="contained"
                        size="small"
                        fullWidth
                        startIcon={<PersonAddIcon />}
                        onClick={handleCreateWorker}
                        sx={{ height: '100%' }}
                      >
                        Add worker
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Workers table */}
                {workers.length === 0 ? (
                  <Alert severity="info">No workers loaded.</Alert>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>User ID</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Telegram</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {workers.map((worker) => {
                        const isEditing = editingWorkerId === worker.id;
                        return (
                          <TableRow key={worker.id} hover>
                            <TableCell>{worker.firstName} {worker.lastName}</TableCell>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography
                                  variant="caption"
                                  sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                                  title={worker.id}
                                >
                                  {worker.id.slice(0, 8)}…
                                </Typography>
                                <Tooltip title="Copy User ID — paste in the Teams tab to add this worker">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      navigator.clipboard?.writeText(worker.id);
                                      setSuccess(`Copied ${worker.firstName} ${worker.lastName}'s User ID.`);
                                    }}
                                    sx={{ p: 0.25 }}
                                  >
                                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              {isEditing ? (
                                <TextField
                                  size="small"
                                  value={editingWorkerEmail}
                                  onChange={(event) => setEditingWorkerEmail(event.target.value)}
                                  fullWidth
                                />
                              ) : worker.email}
                            </TableCell>
                            <TableCell>
                              {isEditing ? (
                                <TextField
                                  size="small"
                                  value={editingWorkerPhone}
                                  onChange={(event) => setEditingWorkerPhone(event.target.value)}
                                  placeholder="+1 876 555 0123"
                                  fullWidth
                                />
                              ) : (worker.phone || <Typography variant="caption" color="text.disabled">—</Typography>)}
                            </TableCell>
                            <TableCell>
                              {isEditing ? (
                                <TextField
                                  size="small"
                                  value={editingWorkerTelegramChatId}
                                  onChange={(event) => setEditingWorkerTelegramChatId(event.target.value)}
                                  placeholder="Telegram chat ID"
                                  fullWidth
                                />
                              ) : (
                                <Stack spacing={0.5}>
                                  <Typography variant="caption" color={worker.telegramChatId ? 'text.primary' : 'text.disabled'}>
                                    {worker.telegramChatId || 'Not linked'}
                                  </Typography>
                                  {telegramLinks[worker.id] && (
                                    <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                      {telegramLinks[worker.id]}
                                    </Typography>
                                  )}
                                </Stack>
                              )}
                            </TableCell>
                            <TableCell>{worker.role}</TableCell>
                            <TableCell align="right">
                              {isEditing ? (
                                <>
                                  <Tooltip title="Save">
                                    <IconButton size="small" onClick={() => handleSaveEditWorker(worker.id)}>
                                      <CheckIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Cancel">
                                    <IconButton size="small" onClick={handleCancelEditWorker}>
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              ) : (
                                <>
                                  <Tooltip title="Create Telegram link">
                                    <IconButton size="small" onClick={() => handleCreateTelegramLink(worker)}>
                                      <LinkIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Edit contact details">
                                    <IconButton size="small" onClick={() => handleStartEditWorker(worker)}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {tab === 'teams' && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Create team
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Team name"
                    value={teamForm.name}
                    onChange={(event) => setTeamForm({ ...teamForm, name: event.target.value })}
                  />
                  <FormControl>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={teamForm.teamType}
                      label="Type"
                      onChange={(event) =>
                        setTeamForm({ ...teamForm, teamType: event.target.value, description: teamForm.description })
                      }
                    >
                      {TEAM_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Description"
                    multiline
                    minRows={3}
                    value={teamForm.description}
                    onChange={(event) => setTeamForm({ ...teamForm, description: event.target.value })}
                  />
                  <TextField
                    label="Max concurrent tasks"
                    type="number"
                    value={teamForm.maxConcurrentTasks}
                    onChange={(event) =>
                      setTeamForm({ ...teamForm, maxConcurrentTasks: Number(event.target.value) || 1 })
                    }
                  />
                  <Button
                    variant="contained"
                    onClick={handleCreateTeam}
                    disabled={teamForm.name.trim().length < 3}
                  >
                    Create team
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={7}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Teams
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Click a team to view or edit its members.
                </Typography>
                {teams.length === 0 ? (
                  <Alert severity="info">No teams loaded.</Alert>
                ) : (
                  <Stack spacing={1}>
                    {teams.map((team) => {
                      const members = team.members ?? [];
                      const draft = memberDrafts[team.id] ?? '';
                      return (
                        <Accordion key={team.id} disableGutters>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1.5}
                              sx={{ width: '100%', pr: 1 }}
                            >
                              <Typography sx={{ fontWeight: 600, flex: 1 }}>
                                {team.name}
                              </Typography>
                              <Chip label={team.teamType} size="small" variant="outlined" />
                              <Chip
                                label={`${members.length} member${members.length === 1 ? '' : 's'}`}
                                size="small"
                              />
                              <Tooltip title="Delete team">
                                <IconButton
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleDeleteTeam(team.id, team.name);
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0 }}>
                            <Stack spacing={1.5}>
                              <Stack
                                direction="row"
                                spacing={2}
                                sx={{ color: 'text.secondary', fontSize: 13 }}
                              >
                                <Box>
                                  Lead: {members.find((m) => m.id === team.teamLeadId)
                                    ? `${members.find((m) => m.id === team.teamLeadId)?.firstName} ${members.find((m) => m.id === team.teamLeadId)?.lastName}`
                                    : team.teamLeadId || 'Unassigned'}
                                </Box>
                                <Box>Max tasks: {team.maxConcurrentTasks ?? '-'}</Box>
                              </Stack>
                              {members.length === 0 ? (
                                <Typography variant="caption" color="text.disabled">
                                  No members yet.
                                </Typography>
                              ) : (
                                <Stack divider={<Divider flexItem />} spacing={0}>
                                  {members.map((m) => (
                                    <Stack
                                      key={m.id}
                                      direction="row"
                                      alignItems="center"
                                      spacing={1}
                                      sx={{ py: 0.5 }}
                                    >
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" noWrap>
                                          {m.firstName} {m.lastName}
                                          {m.id === team.teamLeadId && (
                                            <Chip size="small" label="Team lead" color="primary" sx={{ ml: 1 }} />
                                          )}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap>
                                          {m.email} · {m.role}
                                        </Typography>
                                      </Box>
                                      <Tooltip title="Remove from team">
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleRemoveTeamMember(
                                              team.id,
                                              m.id,
                                              `${m.firstName} ${m.lastName}`
                                            )
                                          }
                                        >
                                          <CloseIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Stack>
                                  ))}
                                </Stack>
                              )}
                              <Divider />
                              <Typography variant="caption" color="text.secondary">
                                Tip: copy a User ID from the <strong>Users</strong> tab using the copy icon, then paste it here.
                              </Typography>
                              {memberAddError[team.id] && (
                                <Alert
                                  severity="error"
                                  onClose={() => setMemberAddError({ ...memberAddError, [team.id]: '' })}
                                >
                                  {memberAddError[team.id]}
                                </Alert>
                              )}
                              <Stack direction="row" spacing={1} alignItems="center">
                                {(() => {
                                  // TeamMemberSummary.id IS the user's id (the row is a
                                  // serialized user, not a join-table record).
                                  const memberIds = new Set(members.map((m) => m.id));
                                  const selectedWorker = workers.find((w) => w.id === draft) ?? null;
                                  // Workers already on this team are filtered out so the picker
                                  // can't double-add. Workers not in the visible list (eg.
                                  // legacy users) are still addable by typing the UUID — the
                                  // freeSolo mode preserves the previous paste-a-UUID escape
                                  // hatch with the same client-side validation downstream.
                                  const availableWorkers = workers.filter((w) => !memberIds.has(w.id));
                                  return (
                                    <Autocomplete
                                      sx={{ flex: 1 }}
                                      size="small"
                                      freeSolo
                                      options={availableWorkers}
                                      value={selectedWorker || (draft && !selectedWorker ? draft : null)}
                                      getOptionLabel={(option) => {
                                        if (typeof option === 'string') return option;
                                        return `${option.firstName} ${option.lastName} (${option.email})`;
                                      }}
                                      isOptionEqualToValue={(option, value) => {
                                        if (typeof option === 'string' || typeof value === 'string') return false;
                                        return option.id === value.id;
                                      }}
                                      onChange={(_, value) => {
                                        if (!value) {
                                          setMemberDrafts({ ...memberDrafts, [team.id]: '' });
                                        } else if (typeof value === 'string') {
                                          setMemberDrafts({ ...memberDrafts, [team.id]: value.trim() });
                                        } else {
                                          setMemberDrafts({ ...memberDrafts, [team.id]: value.id });
                                        }
                                      }}
                                      onInputChange={(_, value, reason) => {
                                        // Allow typing/pasting a raw UUID — only commit to the
                                        // draft when the user stops typing or hits an option;
                                        // selectOption is handled by onChange already.
                                        if (reason === 'input' && /^[a-f0-9-]{36}$/i.test(value.trim())) {
                                          setMemberDrafts({ ...memberDrafts, [team.id]: value.trim() });
                                        }
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Add member"
                                          placeholder="Pick a worker or paste a User ID"
                                          onKeyDown={(event) => {
                                            if (event.key === 'Enter') {
                                              event.preventDefault();
                                              handleAddTeamMember(team.id);
                                            }
                                          }}
                                        />
                                      )}
                                    />
                                  );
                                })()}
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<PersonAddIcon />}
                                  onClick={() => handleAddTeamMember(team.id)}
                                  disabled={!draft.trim()}
                                >
                                  Add
                                </Button>
                              </Stack>
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {tab === 'teams-overview' && <TeamsOverviewPage embedded />}

      {tab === 'projects' && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Projects
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Manager</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projects.map((project) => (
                      <TableRow
                        key={project.id}
                        hover
                        selected={project.id === selectedProjectId}
                        onClick={() => setSelectedProjectId(project.id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{project.projectCode}</TableCell>
                        <TableCell>{project.name}</TableCell>
                        <TableCell>{project.status}</TableCell>
                        <TableCell>{project.projectManagerId || '-'}</TableCell>
                      </TableRow>
                    ))}
                    {projects.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Alert severity="info">No projects loaded.</Alert>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={5}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Project members
                </Typography>
                <Stack spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel>Project</InputLabel>
                    <Select
                      value={selectedProjectId}
                      label="Project"
                      onChange={(event) => setSelectedProjectId(event.target.value)}
                    >
                      {projects.map((project) => (
                        <MenuItem key={project.id} value={project.id}>
                          {project.projectCode} - {project.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="User ID"
                    value={projectMemberUserId}
                    onChange={(event) => setProjectMemberUserId(event.target.value)}
                    placeholder="UUID"
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddProjectMember}
                    disabled={!selectedProjectId || !projectMemberUserId.trim()}
                  >
                    Add member
                  </Button>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {projectMembers.map((member) => (
                        <TableRow key={member.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {member.user ? `${member.user.firstName} ${member.user.lastName}` : member.userId}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {member.user?.email || 'No user details'}
                            </Typography>
                          </TableCell>
                          <TableCell>{member.user?.role || '-'}</TableCell>
                          <TableCell align="right">
                            <Button size="small" color="error" onClick={() => handleRemoveProjectMember(member.userId)}>
                              Remove
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {projectMembers.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3}>
                            <Alert severity="info">No project members loaded.</Alert>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {tab === 'resources' && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Record movement
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Resource ID"
                    value={movementForm.resourceId}
                    onChange={(event) => setMovementForm({ ...movementForm, resourceId: event.target.value })}
                  />
                  <TextField
                    label="Storage location ID"
                    value={movementForm.storageLocationId}
                    onChange={(event) => setMovementForm({ ...movementForm, storageLocationId: event.target.value })}
                  />
                  <FormControl>
                    <InputLabel>Movement type</InputLabel>
                    <Select
                      value={movementForm.movementType}
                      label="Movement type"
                      onChange={(event) => setMovementForm({ ...movementForm, movementType: event.target.value as any })}
                    >
                      {['receipt', 'issue', 'return', 'transfer_out', 'transfer_in', 'adjustment', 'damage', 'theft', 'write_off'].map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Quantity"
                    type="number"
                    value={movementForm.quantity}
                    onChange={(event) => setMovementForm({ ...movementForm, quantity: Number(event.target.value) })}
                  />
                  <TextField
                    label="Notes"
                    multiline
                    minRows={2}
                    value={movementForm.notes}
                    onChange={(event) => setMovementForm({ ...movementForm, notes: event.target.value })}
                  />
                  <Button
                    variant="contained"
                    onClick={handleRecordMovement}
                    disabled={!movementForm.resourceId || !movementForm.storageLocationId || !movementForm.quantity}
                  >
                    Record movement
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Inventory adjustment
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Inventory ID"
                    value={adjustmentForm.inventoryId}
                    onChange={(event) => setAdjustmentForm({ ...adjustmentForm, inventoryId: event.target.value })}
                  />
                  <TextField
                    label="New quantity"
                    type="number"
                    value={adjustmentForm.newQuantity}
                    onChange={(event) => setAdjustmentForm({ ...adjustmentForm, newQuantity: Number(event.target.value) })}
                  />
                  <TextField
                    label="Reason code"
                    value={adjustmentForm.reasonCode}
                    onChange={(event) => setAdjustmentForm({ ...adjustmentForm, reasonCode: event.target.value })}
                  />
                  <TextField
                    label="Notes"
                    multiline
                    minRows={2}
                    value={adjustmentForm.notes}
                    onChange={(event) => setAdjustmentForm({ ...adjustmentForm, notes: event.target.value })}
                  />
                  <Button variant="outlined" onClick={handleAdjustInventory} disabled={!adjustmentForm.inventoryId}>
                    Adjust inventory
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Recent movements
                </Typography>
                <Stack spacing={1}>
                  {movements.slice(0, 6).map((movement) => (
                    <Box key={movement.id}>
                      <Typography variant="body2" fontWeight={600}>
                        {movement.movementType}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {movement.resourceId} at {movement.storageLocationId}
                      </Typography>
                    </Box>
                  ))}
                  {movements.length === 0 && (
                    <Alert severity="info">No inventory movements recorded yet.</Alert>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {tab === 'cost-codes' && (
        <Paper sx={{ p: 2, borderRadius: 1 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Cost codes
          </Typography>
          <Alert severity="info">
            Cost code CRUD is kept to Phase 1 scope, but the backend endpoint is still pending. This page is a read-only placeholder.
          </Alert>
        </Paper>
      )}

      {tab === 'storage' && (
        <Paper sx={{ p: 2, borderRadius: 1 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Storage locations
          </Typography>
          <Alert severity="info">
            Storage location CRUD is part of the admin scope, but the API is not exposed yet. Inventory movements still reference storage IDs directly.
          </Alert>
        </Paper>
      )}

      {tab === 'work-locations' && (
        <Paper sx={{ p: 2, borderRadius: 1 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Work locations
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Managed list of locations PMs can assign to tasks from the Dispatch Board.
            Deleting a location does not delete any tasks — they simply lose the reference.
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="flex-start">
            <TextField
              size="small"
              fullWidth
              label="New location name"
              value={newLocationName}
              onChange={(e) => setNewLocationName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCreateWorkLocation();
                }
              }}
              placeholder='e.g. "Lot 47 — Master Bath", "Block B Foundation"'
            />
            <Button variant="contained" onClick={handleCreateWorkLocation} disabled={!newLocationName.trim()}>
              Add
            </Button>
          </Stack>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align="right" sx={{ width: 140 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workLocations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2}>
                    <Typography variant="body2" color="text.secondary">
                      No work locations yet. Add one above, or create inline from any task card on the Dispatch Board.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                workLocations.map((loc) => (
                  <TableRow key={loc.id} hover>
                    <TableCell>
                      {editingLocationId === loc.id ? (
                        <TextField
                          size="small"
                          fullWidth
                          autoFocus
                          value={editingLocationName}
                          onChange={(e) => setEditingLocationName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleSaveEditWorkLocation();
                            }
                            if (e.key === 'Escape') {
                              setEditingLocationId(null);
                              setEditingLocationName('');
                            }
                          }}
                        />
                      ) : (
                        <Typography variant="body2">{loc.name}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {editingLocationId === loc.id ? (
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Save">
                            <IconButton size="small" onClick={handleSaveEditWorkLocation}>
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditingLocationId(null);
                                setEditingLocationName('');
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ) : (
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Edit name">
                            <IconButton size="small" onClick={() => handleStartEditWorkLocation(loc)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDeleteWorkLocation(loc)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {tab === 'suppliers' && (
        <Paper sx={{ p: 2, borderRadius: 1 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Suppliers
          </Typography>
          <Alert severity="info">
            Supplier CRUD is planned for the minimal admin surface, but the backend surface is not present in this branch.
          </Alert>
        </Paper>
      )}

      <Divider sx={{ my: 2 }} />
      <Typography variant="caption" color="text.secondary">
        Phase 1 keeps user role edits locked, while team and inventory operations remain writable.
      </Typography>
    </Box>
  );
}
